import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Button } from '#app/components/buttons/button.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Button',
}

const component: componentProps = {
	name: 'Button',
	shortName: 'Button',
	fileName: 'button',
	category: 'buttons',
	description: ['A button allows a user to perform an action with mouse, touch, or keyboard.'],
	features: [
		'Mouse, touch, keyboard, and focus interactions are normalized to ensure consistency across browsers and devices.',
		'Uses a native <button> element under the hood, with support for the Space and Enter keys.',
		'Hover, press, and keyboard focus states are provided for easy styling. These states only apply when interacting with an appropriate input device, unlike CSS pseudo classes.',
	],
	parts: [
		{
			name: 'Button',
			description: 'A button allows a user to perform an action with mouse, touch, or keyboard.',
			props: [
				{
					name: 'form',
					type: 'string',
					default: '-',
					description: 'The <form> element to associate the button with. The value of this attribute must be the id of a <form> in the same document.',
				},
				{
					name: 'formAction',
					type: 'string',
					default: '-',
					description: "The URL that processes the information submitted by the button. Overrides the action attribute of the button's form owner.",
				},
				{
					name: 'formEncType',
					type: 'string',
					default: '-',
					description: 'Indicates how to encode the form data that is submitted.',
				},
				{
					name: 'formMethod',
					type: 'string',
					default: '-',
					description: 'Indicates the HTTP method used to submit the form.',
				},
				{
					name: 'formNoValidate',
					type: 'boolean',
					default: '-',
					description: 'Indicates that the form is not to be validated when it is submitted.',
				},
				{
					name: 'formTarget',
					type: 'string',
					default: '-',
					description: "Overrides the target attribute of the button's form owner.",
				},
				{
					name: 'name',
					type: 'string',
					default: '-',
					description: "Submitted as a pair with the button's value as part of the form data.",
				},
				{
					name: 'value',
					type: 'string',
					default: '-',
					description: "The value associated with the button's name when it's submitted with the form data.",
				},
				{
					name: 'isDisabled',
					type: 'boolean',
					default: '-',
					description: 'Whether the button is disabled.',
				},
				{
					name: 'autoFocus',
					type: 'boolean',
					default: '-',
					description: 'Whether the element should receive focus on render.',
				},
				{
					name: 'type',
					type: "'button' | 'submit' | 'reset'",
					default: "'button'",
					description: 'The behavior of the button when used in an HTML form.',
				},
				{
					name: 'children',
					type: 'ReactNode | (values: ButtonRenderProps) => ReactNode',
					default: '-',
					description: 'The children of the component. A function may be provided to alter the children based on component state.',
				},
				{
					name: 'className',
					type: 'string | (values: ButtonRenderProps) => string',
					default: '-',
					description: 'The CSS className for the element. A function may be provided to compute the class based on component state.',
				},
				{
					name: 'style',
					type: 'CSSProperties | (values: ButtonRenderProps) => CSSProperties',
					default: '-',
					description: 'The inline style for the element. A function may be provided to compute the style based on component state.',
				},
			],
			events: [
				{
					name: 'onPress',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when the press is released over the target.',
				},
				{
					name: 'onPressStart',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when a press interaction starts.',
				},
				{
					name: 'onPressEnd',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when a press interaction ends, either over the target or when the pointer leaves the target.',
				},
				{
					name: 'onPressChange',
					type: '(isPressed: boolean) => void',
					description: 'Handler that is called when the press state changes.',
				},
				{
					name: 'onPressUp',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when a press is released over the target, regardless of whether it started on the target or not.',
				},
				{
					name: 'onFocus',
					type: '(e: FocusEvent<Target>) => void',
					description: 'Handler that is called when the element receives focus.',
				},
				{
					name: 'onBlur',
					type: '(e: FocusEvent<Target>) => void',
					description: 'Handler that is called when the element loses focus.',
				},
				{
					name: 'onFocusChange',
					type: '(isFocused: boolean) => void',
					description: "Handler that is called when the element's focus status changes.",
				},
				{
					name: 'onKeyDown',
					type: '(e: KeyboardEvent) => void',
					description: 'Handler that is called when a key is pressed.',
				},
				{
					name: 'onKeyUp',
					type: '(e: KeyboardEvent) => void',
					description: 'Handler that is called when a key is released.',
				},
				{
					name: 'onHoverStart',
					type: '(e: HoverEvent) => void',
					description: 'Handler that is called when a hover interaction starts.',
				},
				{
					name: 'onHoverEnd',
					type: '(e: HoverEvent) => void',
					description: 'Handler that is called when a hover interaction ends.',
				},
				{
					name: 'onHoverChange',
					type: '(isHovering: boolean) => void',
					description: 'Handler that is called when the hover state changes.',
				},
			],
			layout: [
				{
					name: 'slot',
					type: 'string | null',
					description:
						'A slot name for the component. Slots allow the component to receive props from a parent component. An explicit null value indicates that the local props completely override all props received from a parent.',
				},
			],
			state: [
				{
					renderProp: 'isHovered',
					CSSSelector: '[data-hovered]',
					description: 'Whether the button is currently hovered with a mouse.',
				},
				{
					renderProp: 'isPressed',
					CSSSelector: '[data-pressed]',
					description: 'Whether the button is currently in a pressed state.',
				},
				{
					renderProp: 'isFocused',
					CSSSelector: '[data-focused]',
					description: 'Whether the button is focused, either via a mouse or keyboard.',
				},
				{
					renderProp: 'isFocusVisible',
					CSSSelector: '[data-focus-visible]',
					description: 'Whether the button is keyboard focused.',
				},
				{
					renderProp: 'isDisabled',
					CSSSelector: '[data-disabled]',
					description: 'Whether the button is disabled.',
				},
			],
			accessibility: [
				{
					name: 'id',
					type: 'string',
					description: "The element's unique identifier.",
				},
				{
					name: 'excludeFromTabOrder',
					type: 'boolean',
					description:
						'Whether to exclude the element from the sequential tab order. If true, the element will not be focusable via the keyboard by tabbing. This should be avoided except in rare scenarios where an alternative means of accessing the element or its functionality via the keyboard is available.',
				},
				{
					name: 'aria-expanded',
					type: "boolean | 'true' | 'false'",
					description: 'Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed.',
				},
				{
					name: 'aria-haspopup',
					type: "boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | 'true' | 'false'",
					description: 'Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element.',
				},
				{
					name: 'aria-controls',
					type: 'string',
					description: 'Identifies the element (or elements) whose contents or presence are controlled by the current element.',
				},
				{
					name: 'aria-pressed',
					type: "boolean | 'true' | 'false' | 'mixed'",
					description: 'Indicates the current "pressed" state of toggle buttons.',
				},
				{
					name: 'aria-label',
					type: 'string',
					description: 'Defines a string value that labels the current element.',
				},
				{
					name: 'aria-labelledby',
					type: 'string',
					description: 'Identifies the element (or elements) that labels the current element.',
				},
				{
					name: 'aria-describedby',
					type: 'string',
					description: 'Identifies the element (or elements) that describes the object.',
				},
				{
					name: 'aria-details',
					type: 'string',
					description: 'Identifies the element (or elements) that provide a detailed, extended description for the object.',
				},
			],
		},
	],
	usage: `<div className="flex gap-6">
	<Button onPress={() => alert('Hello world!')}>
		Click me
	</Button>
	<Button onPress={() => alert('Hello world!')} variant="secondary">
		Click me
	</Button>
	<Button onPress={() => alert('Hello world!')} variant="danger">
		Click me
	</Button>
	<Button onPress={() => alert('Hello world!')} variant="ghost">
		Click me
	</Button>
</div>
<div className="mt-8 flex gap-6">
	<Button isDisabled onPress={() => alert('Hello world!')}>
		Click me
	</Button>
	<Button isDisabled onPress={() => alert('Hello world!')} variant="secondary">
		Click me
	</Button>
	<Button isDisabled onPress={() => alert('Hello world!')} variant="danger">
		Click me
	</Button>
	<Button isDisabled onPress={() => alert('Hello world!')} variant="ghost">
		Click me
	</Button>
</div>`,
	styling: `.button {
	@apply text-fluid-sm inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-transparent font-semibold transition-colors;

	/* Sizes */
	&.sm {
		@apply text-fluid-xs h-fluid-12 px-fluid-6;
	}
	&.md {
		@apply h-fluid-15 px-fluid-10;
	}
	&.lg {
		@apply text-fluid-lg h-fluid-16 px-fluid-15;
	}

	/* Variants */
	&.primary {
		@apply bg-brand-bg text-brand-text border-brand-text;

		/* States */
		&[data-hovered] {
			@apply bg-brand-bg-hover;
		}
		&[data-pressed] {
			@apply bg-brand-bg-active;
		}
		&[data-disabled] {
			@apply cursor-not-allowed opacity-50;
		}
	}
	&.secondary {
		@apply bg-muted-100 text-muted-800 border-muted-200;

		/* States */
		&[data-hovered] {
			@apply bg-muted-200 border-muted-200;
		}
		&[data-pressed] {
			@apply bg-muted-300 border-muted-200;
		}
		&[data-disabled] {
			@apply cursor-not-allowed opacity-50;
		}
	}
	&.danger {
		@apply border-danger-border bg-danger-bg text-danger-text border;

		/* States */
		&[data-hovered] {
			@apply bg-danger-bg-hover border-danger-border-hover;
		}
		&[data-pressed] {
			@apply bg-danger-bg-active;
		}
		&[data-disabled] {
			@apply cursor-not-allowed opacity-50;
		}
	}
	&.ghost {
		&[data-hovered] {
		}
		&[data-pressed] {
		}
		&[data-disabled] {
			@apply cursor-not-allowed opacity-50;
		}
	}
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const ButtonRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<div className="flex gap-6">
				<Button onPress={() => alert('Hello world!')}>Click me</Button>
				<Button onPress={() => alert('Hello world!')} variant="secondary">
					Click me
				</Button>
				<Button onPress={() => alert('Hello world!')} variant="danger">
					Click me
				</Button>
				<Button onPress={() => alert('Hello world!')} variant="ghost">
					Click me
				</Button>
			</div>
			<div className="mt-8 flex gap-6">
				<Button isDisabled onPress={() => alert('Hello world!')}>
					Click me
				</Button>
				<Button isDisabled onPress={() => alert('Hello world!')} variant="secondary">
					Click me
				</Button>
				<Button isDisabled onPress={() => alert('Hello world!')} variant="danger">
					Click me
				</Button>
				<Button isDisabled onPress={() => alert('Hello world!')} variant="ghost">
					Click me
				</Button>
			</div>
			<Features features={features} />
			<Parts parts={parts} />
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default ButtonRoute
